import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { Role } from '@core/models';
import { environment } from '@env/environment';
import { RoleUser } from '@shared/enums';
import { catchError, Observable, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RoleApiService {
  private readonly baseUrl: string = environment.baseUrl;
  private http = inject(HttpClient);

  private roleMap = signal(new Map<number, string>());

  // Esto se puede consultar desde el back para mayor seguridad si se requiere
  private rolePermissions = signal<Record<RoleUser, { path: string, identifiers: string[] }[]>>({
    [RoleUser.ADMIN]: [
      { path: 'home', identifiers: [] },
      { path: 'home/metrics', identifiers: [] },
      { path: 'home/consolidation', identifiers: ['consolidation'] },

      { path: 'operation', identifiers: [] },
      { path: 'operation/load-files', identifiers: [] },
      { path: 'request', identifiers: [] },
      { path: 'request/list', identifiers: ['save', 'inprocess', 'reject', 'pending_management', 'approve', 'edit'] },
      { path: 'request/create', identifiers: [] },
      { path: 'operation/invoices', identifiers: [] },
      { path: 'simulator', identifiers: [] },
      { path: 'operation/adjustments', identifiers: [] },
      { path: 'config', identifiers: [] },
      { path: 'config/users', identifiers: ['actions', 'updatePassword'] },
      { path: 'config/entities', identifiers: ['actions'] },
      { path: 'config/percentages', identifiers: ['actions'] },
      { path: 'reports', identifiers: [] },
      { path: 'reports/creditos', identifiers: [] },
      { path: 'reports/test', identifiers: [] },
      // { path: 'reports/detalle-fondo', identifiers: [] },
      // { path: 'reports/ses', identifiers: [] },
      // { path: 'reports/reclamaciones', identifiers: [] },
      // { path: 'reports/movimientos', identifiers: [] },
      // { path: 'reports/crobros-juridicos', identifiers: [] },

    ],
    [RoleUser.CLIENT]: [
      { path: 'home', identifiers: ['dashboard'] },
      { path: 'home/metrics', identifiers: ['metrics'] },
      { path: 'simulator', identifiers: ['simulator'] },
      { path: 'operation', identifiers: [] },
      { path: 'reports', identifiers: [] },
      { path: 'reports/creditos', identifiers: [] },
      { path: 'reports/test', identifiers: [] },
      { path: 'operation/load-files', identifiers: [] },
      { path: 'config', identifiers: [] },
      { path: 'config/users', identifiers: ['updatePassword'] },
      { path: 'config/entities', identifiers: [] },
      { path: 'request', identifiers: [] },
      { path: 'request/list', identifiers: ['save', 'correct', 'edit'] },
      { path: 'request/create', identifiers: [] },
    ],
    [RoleUser.LOADER]: [
      { path: 'home', identifiers: ['save'], },
      { path: 'home/metrics', identifiers: ['metrics'] },
      { path: 'operation', identifiers: [] },
      { path: 'operation/load-files', identifiers: ['loadFiles'] },

      { path: 'request', identifiers: [] },
      { path: 'request/list', identifiers: ['save', 'correct'] },
      { path: 'request/create', identifiers: ['create'] },
    ],
    [RoleUser.CONSULTANT]: [
      { path: 'home', identifiers: [] },
      { path: 'home/metrics', identifiers: ['metrics'] },
      { path: 'simulator', identifiers: [] },
      { path: 'request', identifiers: [] },
      { path: 'request/list', identifiers: ['save', 'correct'] },
    ],
    [RoleUser.UNDEFINED]: [
      { path: 'home', identifiers: [] }
    ],
  });

  getRolesMap(): Signal<Map<number, string>> {
    return this.roleMap;
  }

  getRolePermissions(): Signal<Record<number, { path: string, identifiers: string[] }[]>> {
    return this.rolePermissions;
  }

  fetchRoles(): Observable<Role[]> {
    const url = `${this.baseUrl}/roles`;

    return this.http.get<Role[]>(url).pipe(
      tap((roles) => this.roleMap.set(new Map(roles.map((role) => [role.id, role.name])))),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

}
